import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Star, Sparkles, Heart, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Listing {
  id: string;
  title: string;
  description: string;
  address: string;
  price_egp: number;
  deposit_egp: number;
  unit_type: string;
  furnished: boolean;
  gender_rules: string;
  lease_min_months: number;
  available_from: string;
  photos: string[];
  distance_km: number;
  verified: boolean;
  amenities: string[] | null;
  university: {
    name_en: string;
    name_ar: string;
    city: string;
  } | null;
}

const RecommendedForYou = () => {
  const { user, profile } = useAuth();
  const { t, language } = useLanguage();
  const [recommendations, setRecommendations] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(false);
  const [sectionEnabled, setSectionEnabled] = useState(true);

  useEffect(() => {
    checkSectionStatus();
  }, []);

  useEffect(() => {
    if (user && profile && profile.university_id && sectionEnabled) {
      fetchRecommendations();
    }
  }, [user, profile, sectionEnabled]);

  const checkSectionStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('config')
        .select('value')
        .eq('key', 'show_recommended_section')
        .maybeSingle();

      if (error) {
        console.error('Error checking recommendation section status:', error);
        // Default to enabled if there's an error
        setSectionEnabled(true);
        return;
      }

      // If no config found, default to enabled
      if (!data) {
        setSectionEnabled(true);
        return;
      }

      setSectionEnabled(Boolean(data.value));
    } catch (error) {
      console.error('Exception checking recommendation section status:', error);
      setSectionEnabled(true);
    }
  };

  const fetchRecommendations = async () => {
    if (!profile?.university_id) return;
    
    setLoading(true);
    
    try {
      console.log('🔍 Fetching recommendations for university:', profile.university_id);

      // Simple query - only filter by university
      const { data, error } = await supabase
        .from('listings')
        .select(`
          *,
          universities:university_id (
            name_en,
            name_ar,
            city
          )
        `)
        .eq('university_id', profile.university_id)
        .limit(6);

      console.log('✅ Query result:', { data, error, count: data?.length });

      if (error) {
        console.error('❌ Error fetching recommendations:', error);
        return;
      }

      // Transform data to match our interface
      const transformedData = data?.map(listing => ({
        ...listing,
        university: listing.universities
      })) || [];

      console.log('🔄 Transformed data:', transformedData);
      setRecommendations(transformedData);
    } catch (error) {
      console.error('💥 Exception fetching recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Don't show if section is disabled by admin
  if (!sectionEnabled) {
    return null;
  }

  // Don't show if user is not authenticated or hasn't selected university
  if (!user || !profile || !profile.university_id) {
    return null;
  }

  const getUniversityName = (university: any) => {
    if (!university) return '';
    return language === 'ar' ? university.name_ar : university.name_en;
  };

  return (
    <section className="py-16 bg-gradient-to-br from-primary/5 to-accent/5">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="h-6 w-6 text-primary" />
            <h2 className="text-3xl font-bold">
              {t('recommended_for_you', 'مُوصى لك', 'Recommended For You')}
            </h2>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('personalized_recommendations', 
              'عقارات مُختارة بعناية بناءً على جامعتك وتفضيلاتك الشخصية', 
              'Carefully selected properties based on your university and personal preferences'
            )}
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="overflow-hidden animate-pulse">
                <div className="aspect-video bg-muted"></div>
                <CardContent className="p-3 space-y-2">
                  <div className="h-3 bg-muted rounded w-3/4"></div>
                  <div className="h-2 bg-muted rounded w-1/2"></div>
                  <div className="h-2 bg-muted rounded w-full"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : recommendations.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendations.map((listing) => (
              <Card key={listing.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 group">
                <div className="relative aspect-video overflow-hidden">
                  {listing.photos && listing.photos.length > 0 ? (
                    <img
                      src={listing.photos[0]}
                      alt={listing.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                      <MapPin className="h-8 w-8 md:h-12 md:w-12 text-primary/40" />
                    </div>
                  )}
                  
                  {listing.verified && (
                    <Badge className="absolute top-2 left-2 bg-green-500 hover:bg-green-600 text-xs">
                      <Star className="h-2 w-2 mr-1" />
                      <span className="hidden sm:inline">{t('verified', 'موثق', 'Verified')}</span>
                    </Badge>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 h-6 w-6 rounded-full bg-white/80 hover:bg-white p-0"
                  >
                    <Heart className="h-3 w-3" />
                  </Button>
                </div>

                <CardContent className="p-3">
                  <div className="space-y-2">
                    <div>
                      <h3 className="font-semibold text-sm md:text-lg line-clamp-1 group-hover:text-primary transition-colors">
                        {listing.title}
                      </h3>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1 line-clamp-1">
                        <MapPin className="h-2 w-2 flex-shrink-0" />
                        {listing.address}
                      </p>
                    </div>

                    <div className="flex flex-col space-y-2">
                      <div>
                        <p className="text-lg md:text-xl font-bold text-primary">
                          {listing.price_egp.toLocaleString()} {t('egp', 'ج.م', 'EGP')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('per_month', 'شهرياً', 'per month')}
                        </p>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <Badge variant="outline" className="text-xs">
                          {listing.unit_type}
                        </Badge>
                        {listing.distance_km && (
                          <Badge variant="outline" className="text-xs">
                            {listing.distance_km.toFixed(1)} {t('km', 'كم', 'km')}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="pt-2">
                      <Link to={`/listing/${listing.id}`}>
                        <Button size="sm" className="w-full group/btn text-xs">
                          {t('view_details', 'عرض التفاصيل', 'View Details')}
                          <ArrowRight className="h-3 w-3 ml-1 group-hover/btn:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {t('no_recommendations', 'لا توجد توصيات متاحة حالياً', 'No recommendations available at the moment')}
            </p>
            <Link to="/search">
              <Button variant="outline" className="mt-4">
                {t('browse_all_listings', 'تصفح جميع القوائم', 'Browse All Listings')}
              </Button>
            </Link>
          </div>
        )}

        {!loading && recommendations.length > 0 && (
          <div className="text-center mt-8">
            <Link to="/search">
              <Button variant="outline" size="lg">
                {t('view_all_properties', 'عرض جميع العقارات', 'View All Properties')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default RecommendedForYou;